/**
 * Saf hesaplama katmanı — girdi al, çıktı ver, yan etki yok.
 * Test yazacaksan burayı test et; geri kalan her şey görsel ve gözle doğrulanıyor.
 *
 * Vücut kompozisyonu lib/body.ts'te, enerji lib/energy.ts'te — burası ikisini
 * panelin özet görünümü için birleştiriyor.
 */
import { ageFrom } from './body'
import { BASE_PAL, bmr as bmrOf, calorieTarget, proteinTarget, rhoEff } from './energy'
import { todayIso } from './format'

export interface Profile {
  heightCm: number
  age: number
  /** Girilmişse yaş bundan türetiliyor. */
  birthDate: string | null
  sex: 'male' | 'female'
  startWeightKg: number
  targetWeightKg: number
  activityFactor: number
  weeklyRateKg: number
  /** lose | maintain | gain — kalori hedefi ve hız kontrolü buna bağlı. */
  goal: string
  dueDate: string | null      // 'YYYY-MM-DD'
  meal1: string               // 'HH:mm'
  meal2: string
  kitchenClose: string
  strengthPerWeek: number
  makeupWeekday: number
  makeupTime: string
  preTennisMinutes: number
  sessionMinutes: number
  postTennisMinutes: number
  workMinutes: number
  breakMinutes: number
  remindersEnabled: boolean
  /** Kapalı çevrimin biriktirdiği düzeltme (kcal/gün). */
  calorieAdjust: number
  consecutiveCuts: number
  timeZone: string
  updatedAt: string
}

export interface WeightEntry {
  date: string                // 'YYYY-MM-DD'
  kg: number
  note?: string | null
  updatedAt: string
}

export interface Metrics {
  now: number
  bmr: number
  tdee: number
  kcal: number
  protein: number
  proteinPerMeal: number
  bmi: number
  weeksToTarget: number
  lost: number
  remaining: number
}

const DAY = 86_400_000

const tr1 = (n: number) =>
  n.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const WEEK = 7 * DAY

/**
 * Enerji hesabının tek kaynağı lib/energy.ts. Burası sadece o motoru
 * "aktivite kaydı olmadan" çağırıyor — panelin üst kısmındaki özet için.
 * Gerçek sayı (o günün aktiviteleriyle, hata payıyla) Enerji kartında.
 */
export function computeMetrics(p: Profile, weights: WeightEntry[]): Metrics {
  const now = weights.length ? weights[weights.length - 1].kg : p.startWeightKg
  const age = p.birthDate ? ageFrom(p.birthDate, todayIso()) : p.age
  const b = bmrOf(now, p.heightCm, age, p.sex === 'female' ? 'female' : 'male')
  const tdee = b * BASE_PAL

  const rho = rhoEff(now, 32)          // ölçüm yoksa obezitede tipik varsayım
  const t = calorieTarget(tdee, b, p.weeklyRateKg, rho)
  const kcal = t.target

  const lean = now * 0.68              // kaba; kesin değer Enerji kartında
  const protein = proteinTarget(now, p.heightCm, lean).target

  const h = p.heightCm / 100
  const bmi = h > 0 ? now / (h * h) : 0
  const remaining = Math.max(0, now - p.targetWeightKg)
  const weeksToTarget = p.weeklyRateKg > 0 ? remaining / p.weeklyRateKg : 0

  return {
    now,
    bmr: b,
    tdee,
    kcal,
    protein,
    proteinPerMeal: Math.round(protein / 2 / 5) * 5,
    bmi,
    weeksToTarget,
    lost: p.startWeightKg - now,
    remaining,
  }
}

/**
 * Bebekle ilgili bölümler (Bebek Planı sekmesi, faz kartı, hamilelik notları)
 * yalnızca doğum tarihi girilmişse görünür.
 *
 * Ayrı bir "bunu göster/gizleme" anahtarı KOYMUYORUZ: veri zaten cevabı içeriyor.
 * Bekleyen bebeği olmayan bir kullanıcı o alanı boş bırakır ve bu bölümleri hiç
 * görmez; olan girer ve görür. Anahtar eklemek, aynı bilgiyi iki yerde tutup
 * senkron kalmasını ummak demekti.
 */
export function hasBabyPhase(p: Profile): boolean {
  return !!p.dueDate
}

/**
 * Profil kurulumu tamamlandı mı.
 *
 * Yeni hesapta bu alanlar SIFIR geliyor (bkz. Profile entity). Sıfırla hesap
 * yapmak sessiz saçmalık üretiyor: VKİ sonsuz, bazal metabolizma negatif,
 * hedef kalori tabana yapışık. Panel bu yüzden hesap yapmadan önce kurulum
 * ekranından geçiyor — yanlış sayı, sayı yokluğundan kötüdür.
 */
export function profileComplete(p: Profile): boolean {
  return p.heightCm >= 100 && p.heightCm <= 250
    && p.startWeightKg >= 30 && p.targetWeightKg >= 30
    && (!!p.birthDate || p.age >= 10)
}

export type PhaseId = 'build' | 'protect' | 'resume'

export interface Phase {
  id: PhaseId
  name: string
  title: string
  body: string
  daysToBirth: number | null
}

/**
 * Doğum tarihine göre faz. Gerekçe: bölünmüş uykuda ghrelin yükselir, leptin düşer —
 * o dönemde kalori açığı vermek çoğu insanda başarısız oluyor ve planın tamamen
 * bırakılmasıyla bitiyor. Faz 2'yi baştan "koruma" olarak tanımlamak bunu önlüyor.
 */
export function computePhase(p: Profile, m: Metrics, today = new Date()): Phase {
  if (!p.dueDate) {
    return {
      id: 'build',
      name: 'Faz 1 — İnşa',
      title: 'Plan tam kapasite',
      body: 'Doğum tarihi girilmemiş. Profil bölümünden girersen panel fazları otomatik yönetir.',
      daysToBirth: null,
    }
  }

  const birth = new Date(p.dueDate + 'T00:00:00')
  // Yerel gün — toISOString() UTC'ye çevirip geceyarısı civarında bir gün kaydırır.
  const pad = (n: number) => String(n).padStart(2, '0')
  const todayLocal = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
  const t0 = new Date(todayLocal + 'T00:00:00')
  const days = Math.round((birth.getTime() - t0.getTime()) / DAY)
  const weeks = Math.max(0, Math.round(days / 7))

  if (days > 0) {
    const proj = Math.max(p.targetWeightKg, m.now - p.weeklyRateKg * (days / 7))
    return {
      id: 'build',
      name: `Faz 1 — İnşa · doğuma ${weeks} hafta`,
      title: 'Şimdi tam gaz, çünkü sonra 3 ay duracaksın',
      body:
        `Bu ${weeks} hafta boyunca planı eksiksiz uygularsan doğum gününde ≈ ${tr1(proj)} kg olursun. ` +
        `Bu dönemin asıl işi kilo vermek değil: alışkanlığı otomatikleştirmek ve dondurucuyu doldurmak. ` +
        `Uykusuz gecelerde iradene değil, o stoğa güveneceksin.`,
      daysToBirth: days,
    }
  }

  if (days > -100) {
    return {
      id: 'protect',
      name: `Faz 2 — Koruma · doğumdan ${Math.abs(Math.round(days / 7))} hafta sonra`,
      title: 'Hedef kilo vermek değil, kilo ALMAMAK',
      body:
        `Kaloriyi bakım seviyesine (≈ ${Math.round(m.tdee / 10) * 10} kcal) çek, tartıyı ayda bir kere gör, ` +
        `protein ve yürüyüşü koru. Aynı kiloyla çıkmak bu fazda tam not.`,
      daysToBirth: days,
    }
  }

  return {
    id: 'resume',
    name: 'Faz 3 — Yeniden hızlan',
    title: 'Uyku oturdu, açık geri gelebilir',
    body:
      `Kalori hedefini tekrar ${Math.round(m.kcal)} kcal'e indir, antrenman ve tenisi tam kapasiteye çıkar. ` +
      `Faz 1'de kurduğun alışkanlıklar hâlâ yerinde — sıfırdan başlamıyorsun.`,
    daysToBirth: days,
  }
}

export interface Milestone {
  label: string
  kg: number
  why: string
  done: boolean
  remaining: number
}

export function computeMilestones(p: Profile, m: Metrics): Milestone[] {
  const h = p.heightCm / 100
  const raw = [
    { label: 'İlk %5', kg: p.startWeightKg * 0.95, why: 'Kan şekeri ve tansiyonda ölçülebilir düzelme burada başlar' },
    { label: 'VKİ 35', kg: 35 * h * h, why: 'Diz ve bel yükü belirgin azalır, tenis kolaylaşır' },
    { label: 'VKİ 30', kg: 30 * h * h, why: 'Obezite aralığından çıkış — en büyük sağlık sıçraması' },
    { label: 'Hedef', kg: p.targetWeightKg, why: 'Buradan sonrası korumak, yeniden başlamak değil' },
  ]

  return raw
    .filter((x) => x.kg < p.startWeightKg - 0.5)
    .sort((a, b) => b.kg - a.kg)
    .filter((x, i, arr) => i === 0 || Math.abs(x.kg - arr[i - 1].kg) > 3)
    .map((x) => ({
      ...x,
      done: m.now <= x.kg,
      remaining: Math.max(0, m.now - x.kg),
    }))
}

export interface TargetPoint { ms: number; kg: number }

/** Hedef iniş eğrisi — başlangıç tarihinden itibaren haftada weeklyRateKg. */
export function targetCurve(p: Profile, startMs: number, endMs: number): TargetPoint[] {
  const pts: TargetPoint[] = []
  const step = 3.5 * DAY
  for (let t = startMs; t <= endMs; t += step) {
    pts.push({ ms: t, kg: Math.max(p.targetWeightKg, p.startWeightKg - p.weeklyRateKg * ((t - startMs) / WEEK)) })
  }
  const last = pts[pts.length - 1]
  if (!last || last.ms < endMs) {
    pts.push({ ms: endMs, kg: Math.max(p.targetWeightKg, p.startWeightKg - p.weeklyRateKg * ((endMs - startMs) / WEEK)) })
  }
  return pts
}

/** Bir tartının hedef eğrisine göre farkı. Negatif = önde. */
export function offTarget(p: Profile, firstDateMs: number, entry: WeightEntry): number {
  const weeks = (new Date(entry.date).getTime() - firstDateMs) / WEEK
  return entry.kg - (p.startWeightKg - p.weeklyRateKg * weeks)
}

/** Son n kaydın ortalaması — günlük dalgalanmayı yumuşatır. */
export function movingAverage(weights: WeightEntry[], n = 7): number | null {
  if (!weights.length) return null
  const slice = weights.slice(-n)
  return slice.reduce((s, w) => s + w.kg, 0) / slice.length
}
